import React, { useState, useEffect, useContext, useCallback, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import "./CataloguePage.css";
import { UserContext } from "../../context/UserContext";
import ApiService from "../../services/ApiService";
import NotificationService from "../../components/NotificationService/NotificationService";
import back from "../../img/icons/BeigeArrowPrev.png";
import forward from "../../img/icons/BeigeArrowNext.png";
import ValueTiles from "../../components/ValueTiles/ValueTiles";

// Category filter options
const CATEGORY_OPTIONS = [
  'General', 'Technique', 'Culture', 'Events', 'Personal', 'Education', 'Community'
];

const MAX_FILTERS = 7;

const guideSteps = [
  {
    text: "We see musing about dance like a generous flow of sap! But for our blog posts we are inviting you to share the maple syrup thoughts that can heal hearts, or trigger profound conversations in our communities. Therefore please enjoy the playground of",
    highlight: "500-1500 words"
  },
  {
    text: "Decide whether you would like to publish it under your name or as a",
    highlight: "Move n Muse community member"
  },
  {
    text: "Need more time to finish your musings? Your draft is",
    highlight: "automatically saved",
    extra: "to your account. You have 2 weeks to complete your article before the topic becomes available again."
  },
  {
    text: "We know that it's a time of heavy AI content generation, but this is a place of",
    highlight: "authenticity",
    extra: ". Please muse with your own beautiful voice."
  },
  {
    text: "Add",
    highlight: "5-10 tags",
    extra: "to make it easier for others to find your article."
  },
  {
    text: "In the future you will be invited to add an",
    highlight: "audio recording",
    extra: "of yourself reading your musings. (Coming up!)"
  }
];

export default function CataloguePage() {
  const { user } = useContext(UserContext);
  const navigate = useNavigate();

  // State
  const [topics, setTopics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedFilters, setSelectedFilters] = useState([]);
  const [filterSearch, setFilterSearch] = useState("");
  const [showFilterSuggestions, setShowFilterSuggestions] = useState(false);
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [newTopicName, setNewTopicName] = useState("");
  const [newTopicCategory, setNewTopicCategory] = useState("general");

  // Modal states
  const [showTopicModal, setShowTopicModal] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showAddTopicModal, setShowAddTopicModal] = useState(false);
  const [userHasActiveDraft, setUserHasActiveDraft] = useState(false);
  const [activeDraftTopic, setActiveDraftTopic] = useState(null);

  const topicsPerPage = 12;
  const isLoggedIn = !!user;

  // Scroll to top on page load
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Fetch topics from the Topics collection (not the old Titles collection)
  useEffect(() => {
    const fetchTopics = async () => {
      try {
        setLoading(true);
        // Use Topics API which returns only available and approved topics
        const response = await ApiService.get("topics");
        const data = response?.data?.data || [];
        setTopics(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error("Error fetching topics:", error);
        NotificationService.notifyError("Failed to load topics");
      } finally {
        setLoading(false);
      }
    };

    fetchTopics();
  }, []);

  // Check for active drafts when user changes
  useEffect(() => {
    const checkActiveDrafts = async () => {
      if (!user) {
        setUserHasActiveDraft(false);
        setActiveDraftTopic(null);
        return;
      }

      try {
        const response = await ApiService.get("topics/user/my-topics");
        const data = response?.data?.data;

        if (data) {
          const reservedTopics = data.reservedTopics || [];
          const drafts = data.drafts || [];

          if (reservedTopics.length > 0 || drafts.length > 0) {
            setUserHasActiveDraft(true);
            setActiveDraftTopic(
              reservedTopics[0] ||
              { name: drafts[0]?.articleTitle || drafts[0]?.topic?.name }
            );
          } else {
            setUserHasActiveDraft(false);
            setActiveDraftTopic(null);
          }
        }
      } catch (error) {
        console.error("Error checking drafts:", error);
      }
    };

    checkActiveDrafts();
  }, [user]);

  // Check if we can add more filters
  const canAddMoreFilters = selectedFilters.length < MAX_FILTERS;

  // Filter suggestions based on search input
  const filterSuggestions = useMemo(() => {
    const available = CATEGORY_OPTIONS.filter(cat => !selectedFilters.includes(cat));
    if (!filterSearch.trim()) return available;
    const searchLower = filterSearch.toLowerCase();
    return available.filter(cat => cat.toLowerCase().includes(searchLower));
  }, [filterSearch, selectedFilters]);

  // Add a filter tag
  const addFilter = (filter) => {
    const trimmedFilter = filter.trim();
    if (trimmedFilter && !selectedFilters.includes(trimmedFilter) && canAddMoreFilters) {
      setSelectedFilters(prev => [...prev, trimmedFilter]);
    }
    setFilterSearch('');
    setShowFilterSuggestions(false);
    setCurrentPage(1);
  };

  // Handle Enter key for filter input
  const handleFilterKeyDown = (e) => {
    if (e.key === 'Enter' && filterSearch.trim() && canAddMoreFilters) {
      e.preventDefault();
      addFilter(filterSearch);
    }
  };

  // Remove a filter tag
  const removeFilter = (filter) => {
    setSelectedFilters(prev => prev.filter(f => f !== filter));
    setCurrentPage(1);
  };

  // Clear all filters
  const clearFilters = () => {
    setSelectedFilters([]);
    setFilterSearch('');
    setCurrentPage(1);
  };

  const hasActiveFilters = selectedFilters.length > 0;

  // Filter topics
  const filteredTopics = useMemo(() => {
    if (selectedFilters.length === 0) return topics;

    return topics.filter(topic => {
      const topicCategory = (topic.category || 'general').toLowerCase();
      const topicName = (topic.name || '').toLowerCase();

      return selectedFilters.some(filter => {
        const filterLower = filter.toLowerCase();
        // Match by category or by topic name containing the filter
        return topicCategory === filterLower || topicName.includes(filterLower);
      });
    });
  }, [topics, selectedFilters]);

  // Pagination
  const totalPages = Math.ceil(filteredTopics.length / topicsPerPage);
  const startIndex = (currentPage - 1) * topicsPerPage;
  const currentTopics = filteredTopics.slice(startIndex, startIndex + topicsPerPage);

  const handlePrevPage = useCallback(() => {
    if (currentPage > 1) setCurrentPage(prev => prev - 1);
  }, [currentPage]);

  const handleNextPage = useCallback(() => {
    if (currentPage < totalPages) setCurrentPage(prev => prev + 1);
  }, [currentPage, totalPages]);

  // Topic selection
  const handleTopicClick = useCallback((topic) => {
    setSelectedTopic(topic);

    if (!isLoggedIn) {
      setShowLoginModal(true);
    } else {
      setShowTopicModal(true);
    }
  }, [isLoggedIn]);

  // Start writing
  const handleStartWriting = useCallback(async () => {
    if (!selectedTopic || !user) return;

    try {
      const response = await ApiService.post(`topics/${selectedTopic._id}/reserve`);

      if (response?.data?.success) {
        setShowTopicModal(false);
        NotificationService.notifySuccess("Topic reserved! Redirecting...");
        navigate(`/write?topic=${selectedTopic._id}`);
      } else {
        NotificationService.notifyError(response?.data?.message || "Failed to reserve topic");
      }
    } catch (error) {
      console.error("Error reserving topic:", error);
      NotificationService.notifyError("Failed to reserve topic");
    }
  }, [selectedTopic, user, navigate]);

  // Add new topic
  const handleAddTopic = useCallback(async (startWritingNow = false) => {
    if (!newTopicName.trim()) {
      NotificationService.notifyError("Please enter a topic name");
      return;
    }

    if (!isLoggedIn) {
      setShowAddTopicModal(false);
      setShowLoginModal(true);
      return;
    }

    try {
      const response = await ApiService.post("topics", {
        name: newTopicName.trim(),
        category: newTopicCategory
      });

      if (response?.data?.success) {
        const newTopic = response?.data?.data;
        setShowAddTopicModal(false);
        setNewTopicName("");

        if (startWritingNow && newTopic?._id) {
          // Reserve and start writing
          await ApiService.post(`topics/${newTopic._id}/reserve`);
          navigate(`/write?topic=${newTopic._id}`);
        } else {
          NotificationService.notifySuccess("Topic submitted for review!");
          // Refresh topics
          const topicsResponse = await ApiService.get("getAllTitles");
          const data = topicsResponse?.data?.data?.titles || [];
          setTopics(Array.isArray(data) ? data : []);
        }
      } else {
        NotificationService.notifyError(response?.data?.message || "Failed to create topic");
      }
    } catch (error) {
      console.error("Error adding topic:", error);
      NotificationService.notifyError("Failed to create topic");
    }
  }, [newTopicName, newTopicCategory, isLoggedIn, navigate]);

  // Close modals on escape
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape") {
        setShowTopicModal(false);
        setShowLoginModal(false);
        setShowAddTopicModal(false);
      }
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, []);

  return (
    <div className="catalogue-page">
      <main className="container">
        {/* Header */}
        <section className="catalogue-header">
          <h1 className="catalogue-title">
            Move n Muse Catalogue
            <span className="blog-badge">BLOG</span>
          </h1>

          <div className="instructions">
            <p>- Every topic in this catalogue is waiting for submissions from our valuable readers, writers, and dancers.</p>
            <p>- Can't find something that speaks to you? Feel free to <strong>create your own!</strong></p>
            <p>- Submit your article. This will make the topic unavailable for others to pick.</p>
            <p>- Our dedicated Move n Muse team will go over every piece of writing to ensure that it aligns with Move n Muse community values and reach out back to you.</p>
          </div>
        </section>

        {/* Values Section */}
        <section className="values-section">
          <div className="values-label">OUR VALUES</div>
          <ValueTiles />
        </section>

        {/* Filter Section */}
        <section className="filter-section">
          <div className="filter-bar">
            <span className="filter-count">
              {filteredTopics.length} Topic{filteredTopics.length !== 1 ? 's' : ''}
            </span>

            <div className="filter-controls">
              {/* Add Filter Input */}
              <div className="filter-input-wrapper">
                <input
                  type="text"
                  className="filter-select filter-input"
                  placeholder={canAddMoreFilters ? "+ Add filter" : "Max 7 filters"}
                  value={filterSearch}
                  onChange={(e) => setFilterSearch(e.target.value)}
                  onKeyDown={handleFilterKeyDown}
                  onFocus={() => canAddMoreFilters && setShowFilterSuggestions(true)}
                  onBlur={() => setTimeout(() => setShowFilterSuggestions(false), 200)}
                  disabled={!canAddMoreFilters}
                />
                {showFilterSuggestions && filterSuggestions.length > 0 && (
                  <div className="filter-dropdown">
                    {filterSuggestions.slice(0, 8).map((filter) => (
                      <button
                        key={filter}
                        className="filter-dropdown-item"
                        onClick={() => addFilter(filter)}
                      >
                        {filter}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {hasActiveFilters && (
                <button className="clear-filters-btn" onClick={clearFilters}>
                  Clear
                </button>
              )}
            </div>
          </div>

          {/* Selected Filters Row */}
          {selectedFilters.length > 0 && (
            <div className="filter-tags-row">
              {selectedFilters.map((filter) => (
                <span key={filter} className="filter-tag">
                  {filter}
                  <button className="filter-tag-remove" onClick={() => removeFilter(filter)}>×</button>
                </span>
              ))}
            </div>
          )}
        </section>

        {/* Topics List */}
        <section className="topics-container">
          <div className="topics-header">
            <h2 className="topics-title">Available Topics</h2>
            <span className="topics-count">
              {loading ? "Loading..." : `${filteredTopics.length} topics available`}
            </span>
          </div>

          <div className="topics-list">
            {loading ? (
              <div className="loading-spinner">
                <div className="spinner"></div>
              </div>
            ) : currentTopics.length === 0 ? (
              <div className="empty-state">
                <i className="bi bi-journal-x"></i>
                <p>No topics found. Be the first to create one!</p>
              </div>
            ) : (
              currentTopics.map((topic, index) => (
                <div
                  key={topic._id}
                  className={`topic-item ${selectedTopic?._id === topic._id ? 'selected' : ''}`}
                  onClick={() => handleTopicClick(topic)}
                >
                  <span className="topic-number">{startIndex + index + 1}.</span>
                  <span className="topic-name">{topic.name}</span>
                  <span className="topic-category">{topic.category || 'general'}</span>
                </div>
              ))
            )}
          </div>

          {/* Add Topic */}
          <div className="add-topic-container">
            <div
              className="add-topic-icon"
              onClick={() => setShowAddTopicModal(true)}
            >
              <i className="bi bi-plus"></i>
            </div>
            <span
              className="add-topic-text"
              onClick={() => setShowAddTopicModal(true)}
            >
              Add your topic
            </span>
          </div>

          {/* Pagination */}
          {filteredTopics.length > topicsPerPage && (
            <div className="pagination-container">
              <button
                className="page-btn"
                onClick={handlePrevPage}
                disabled={currentPage === 1}
              >
                <img src={back} alt="Previous" />
              </button>
              <button
                className="page-btn"
                onClick={handleNextPage}
                disabled={currentPage >= totalPages}
              >
                <img src={forward} alt="Next" />
              </button>
            </div>
          )}
        </section>

        {/* Musing Guide */}
        <section className="musing-guide">
          <h3 className="musing-guide-title">Musing Guide</h3>
          <ol>
            {guideSteps.map((step, index) => (
              <li key={index}>
                {step.text} <strong>{step.highlight}</strong>
                {step.extra && ` ${step.extra}`}
              </li>
            ))}
          </ol>
        </section>
      </main>

      {/* Topic Selected Modal */}
      {showTopicModal && selectedTopic && (
        <div className="modal-backdrop" onClick={() => setShowTopicModal(false)}>
          <div className="modal-content-custom" onClick={(e) => e.stopPropagation()}>
            <button
              className="modal-close-btn"
              onClick={() => setShowTopicModal(false)}
            >
              <i className="bi bi-x-lg"></i>
            </button>
            <i className="bi bi-journal-text modal-icon"></i>
            <h3 className="modal-title">Ready to Write?</h3>
            <p className="text-muted mb-2">You've selected:</p>
            <div className="selected-topic-name">{selectedTopic.name}</div>
            <p className="text-muted mb-3">
              This topic will be reserved for you for 2 weeks while you write your article.
            </p>

            {userHasActiveDraft && activeDraftTopic && (
              <div className="active-draft-warning">
                <i className="bi bi-info-circle me-1"></i>
                You already have an ongoing article: <strong>"{activeDraftTopic.name}"</strong>
                <br />
                <Link to={`/write?topic=${activeDraftTopic._id}`}>Continue writing</Link> or delete it from your <Link to="/profile">profile</Link> first.
              </div>
            )}

            <button
              className="modal-btn"
              onClick={handleStartWriting}
              disabled={userHasActiveDraft}
            >
              <i className="bi bi-pencil-fill"></i> Start Writing
            </button>

            <p className="mt-2 text-muted small">
              Not ready yet?{' '}
              <button
                className="modal-link"
                onClick={() => setShowTopicModal(false)}
                style={{ background: 'none', border: 'none', cursor: 'pointer' }}
              >
                Choose a different topic
              </button>
            </p>
          </div>
        </div>
      )}

      {/* Login Modal */}
      {showLoginModal && (
        <div className="modal-backdrop" onClick={() => setShowLoginModal(false)}>
          <div className="modal-content-custom" onClick={(e) => e.stopPropagation()}>
            <button
              className="modal-close-btn"
              onClick={() => setShowLoginModal(false)}
            >
              <i className="bi bi-x-lg"></i>
            </button>
            <i className="bi bi-person-circle modal-icon"></i>
            <h3 className="modal-title">Sign In to Write</h3>
            {selectedTopic && (
              <>
                <p className="text-muted mb-2">You've selected the topic:</p>
                <div className="selected-topic-name">{selectedTopic.name}</div>
              </>
            )}
            <p className="text-muted mb-3">
              Please sign in to reserve this topic and start writing.
            </p>

            <Link to="/login" className="modal-btn">
              <i className="bi bi-box-arrow-in-right"></i> Sign In
            </Link>

            <p className="mt-2 text-muted small">
              Don't have an account?{' '}
              <Link to="/login" className="modal-link">Sign Up</Link>
            </p>
          </div>
        </div>
      )}

      {/* Add Topic Modal */}
      {showAddTopicModal && (
        <div className="modal-backdrop" onClick={() => setShowAddTopicModal(false)}>
          <div className="modal-content-custom" onClick={(e) => e.stopPropagation()}>
            <button
              className="modal-close-btn"
              onClick={() => setShowAddTopicModal(false)}
            >
              <i className="bi bi-x-lg"></i>
            </button>
            <i className="bi bi-lightbulb modal-icon" style={{ color: 'var(--sage-green)' }}></i>
            <h3 className="modal-title">Propose a Topic</h3>
            <p className="text-muted mb-3">
              Share your idea for a dance-related topic you'd like to write about.
            </p>

            <div className="mb-3">
              <input
                type="text"
                className="form-control mb-2"
                placeholder="Enter your topic idea..."
                value={newTopicName}
                onChange={(e) => setNewTopicName(e.target.value)}
                maxLength={100}
              />
              <select
                className="form-control mb-2"
                value={newTopicCategory}
                onChange={(e) => setNewTopicCategory(e.target.value)}
              >
                <option value="general">General</option>
                <option value="technique">Technique</option>
                <option value="culture">Culture</option>
                <option value="history">History</option>
                <option value="wellbeing">Wellbeing</option>
                <option value="events">Events</option>
                <option value="personal">Personal</option>
                <option value="education">Education</option>
                <option value="community">Community</option>
              </select>

              {userHasActiveDraft && activeDraftTopic && (
                <div className="active-draft-warning">
                  <i className="bi bi-info-circle me-1"></i>
                  You already have an ongoing article: <strong>"{activeDraftTopic.name}"</strong>
                  <br />
                  <Link to={`/write?topic=${activeDraftTopic._id}`}>Continue writing</Link> or delete it from your <Link to="/profile">profile</Link> first.
                </div>
              )}

              <div className="d-flex gap-2">
                <button
                  className="modal-btn sage flex-fill"
                  onClick={() => handleAddTopic(false)}
                >
                  <i className="bi bi-send"></i> Submit Topic
                </button>
                <button
                  className="modal-btn flex-fill"
                  onClick={() => {
                    if (userHasActiveDraft) {
                      NotificationService.notifyError('Please finish your current draft first or release it from your profile.');
                      return;
                    }
                    handleAddTopic(true);
                  }}
                  style={userHasActiveDraft ? { opacity: 0.6, cursor: 'not-allowed' } : {}}
                >
                  <i className="bi bi-pencil-fill"></i> Start Writing Now
                </button>
              </div>
            </div>

            <p className="mt-2 text-muted small">
              Submit Topic so the community can work on it, or Start Writing Now.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
